import { useState } from "react";
import { Card, Tabs, Text } from "@mantine/core";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import type { DetailedAnalytics } from "../types";
import type { AnalyticsPeriod } from "../types";

// Helper function to get date range labels
const getDateRangeLabel = (key: string): string => {
  const now = new Date();

  if (key === "allTime") {
    return "All Time";
  } else if (key === "month") {
    const monthAgo = new Date(now);
    monthAgo.setDate(now.getDate() - 30);
    return `${monthAgo.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${now.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  } else if (key === "week") {
    const weekAgo = new Date(now);
    weekAgo.setDate(now.getDate() - 7);
    return `${weekAgo.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${now.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  } else if (key === "day") {
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    return `${yesterday.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${now.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  }

  return key;
};

interface DetailedMetricsChartProps {
  detailed: DetailedAnalytics;
  period: AnalyticsPeriod;
}

export const DetailedMetricsChart = ({
  detailed,
  period,
}: DetailedMetricsChartProps) => {
  const [activeTab, setActiveTab] = useState<string | null>("ratings");

  // Get data based on selected period
  const getFilteredData = () => {
    const allData = [
      {
        period: "All Time",
        key: "allTime" as const,
      },
      {
        period: "This Month",
        key: "month" as const,
      },
      {
        period: "This Week",
        key: "week" as const,
      },
      {
        period: "Today",
        key: "day" as const,
      },
    ];

    // Filter based on selected period
    let filteredPeriods = allData;
    if (period === "month") {
      filteredPeriods = allData.slice(1); // Remove "All Time"
    } else if (period === "week") {
      filteredPeriods = allData.slice(2); // Remove "All Time" and "This Month"
    } else if (period === "day") {
      filteredPeriods = allData.slice(3); // Only "Today"
    }

    return filteredPeriods;
  };

  const filteredPeriods = getFilteredData();

  // Prepare data for Rating Scores chart
  const ratingsData = filteredPeriods.map(({ key }) => ({
    period: getDateRangeLabel(key),
    TaxSlayer: detailed.taxslayer[key].avgRating || 0,
    TaxWise: detailed.taxwise[key].avgRating || 0,
  }));

  // Prepare data for Jobs chart (combined success and failed)
  const jobsData = filteredPeriods.map(({ key }) => ({
    period: getDateRangeLabel(key),
    "Successful Jobs": detailed.taxslayer[key].successJobs,
    "Failed Jobs": detailed.taxslayer[key].failedJobs,
  }));

  // Prepare data for Total Returns chart
  const totalReturnsData = filteredPeriods.map(({ key }) => ({
    period: getDateRangeLabel(key),
    TaxSlayer: detailed.taxslayer[key].totalReturns,
    TaxWise: detailed.taxwise[key].totalReturns,
  }));

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Text fw={700} size="xl" mb="md">
        Metrics Over Time
      </Text>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="ratings">Rating Scores</Tabs.Tab>
          <Tabs.Tab value="jobs">Jobs</Tabs.Tab>
          <Tabs.Tab value="totalReturns">Total Returns</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="ratings" pt="md">
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={ratingsData} margin={{ bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="period"
                angle={-45}
                textAnchor="end"
                height={80}
                tick={{ fontSize: 12 }}
              />
              <YAxis domain={[0, 5]} />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="TaxSlayer"
                stroke="#339AF0"
                strokeWidth={2}
                dot={{ r: 5 }}
                activeDot={{ r: 7 }}
              />
              <Line
                type="monotone"
                dataKey="TaxWise"
                stroke="#51CF66"
                strokeWidth={2}
                dot={{ r: 5 }}
                activeDot={{ r: 7 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Tabs.Panel>

        <Tabs.Panel value="jobs" pt="md">
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={jobsData} margin={{ bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="period"
                angle={-45}
                textAnchor="end"
                height={80}
                tick={{ fontSize: 12 }}
              />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="Successful Jobs"
                stroke="#51CF66"
                strokeWidth={2}
                dot={{ r: 5 }}
                activeDot={{ r: 7 }}
              />
              <Line
                type="monotone"
                dataKey="Failed Jobs"
                stroke="#FA5252"
                strokeWidth={2}
                dot={{ r: 5 }}
                activeDot={{ r: 7 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Tabs.Panel>

        <Tabs.Panel value="totalReturns" pt="md">
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={totalReturnsData} margin={{ bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="period"
                angle={-45}
                textAnchor="end"
                height={80}
                tick={{ fontSize: 12 }}
              />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="TaxSlayer"
                stroke="#339AF0"
                strokeWidth={2}
                dot={{ r: 5 }}
                activeDot={{ r: 7 }}
              />
              <Line
                type="monotone"
                dataKey="TaxWise"
                stroke="#51CF66"
                strokeWidth={2}
                dot={{ r: 5 }}
                activeDot={{ r: 7 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Tabs.Panel>
      </Tabs>
    </Card>
  );
};
